import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient, { getUploadUrl } from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';
import { CheckCircle, XCircle, Eye, FileText, Image, File, ExternalLink, Clock, Ban, Trash2, AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const AdminApprovals = () => {
  const [tab, setTab] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [viewingApp, setViewingApp] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [reconsidering, setReconsidering] = useState(null);

  const loadApplications = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/applications/pending');
      setApplications(res.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRejected = useCallback(async () => {
    setRejectedLoading(true);
    try {
      const res = await apiClient.get('/admin/applications/rejected');
      setRejected(res.data);
    } catch (error) {
      toast.error('Failed to load rejected applications');
    } finally {
      setRejectedLoading(false);
    }
  }, []);

  useEffect(() => { loadApplications(); }, [loadApplications]);
  useEffect(() => { if (tab === 'rejected') loadRejected(); }, [tab, loadRejected]);

  const handleReview = async (applicationId, status) => {
    try {
      await apiClient.post(`/admin/applications/${applicationId}/review`, {
        status,
        review_notes: `Application ${status}`,
      });
      toast.success(`Application ${status} successfully`);
      loadApplications();
    } catch (error) {
      toast.error('Failed to review application');
    }
  };

  const deleteApplication = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/applications/${deleteTarget.application_id}`);
      toast.success('Application deleted');
      setDeleteTarget(null);
      loadRejected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    } finally {
      setDeleting(false);
    }
  };

  const reconsiderApplication = async (app) => {
    setReconsidering(app.application_id);
    try {
      await apiClient.post(`/admin/applications/${app.application_id}/reconsider`);
      toast.success('Application moved back to Pending');
      loadRejected();
      loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reconsider application');
    } finally {
      setReconsidering(null);
    }
  };

  const deleteAllRejected = async () => {
    setDeletingAll(true);
    try {
      const res = await apiClient.delete('/admin/applications/rejected/all');
      toast.success(res.data.message || 'All rejected applications deleted');
      setShowDeleteAll(false);
      loadRejected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rejected applications');
    } finally {
      setDeletingAll(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType?.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const getAttachmentLabel = (index) => {
    const labels = ['ID Copy', 'Portrait Photo', 'Proof of Payment'];
    return labels[index] || `Attachment ${index + 1}`;
  };

  const openFileViewer = (attachment, index) => {
    const url = getUploadUrl(attachment.file_path);
    if (!url) {
      toast.error('File not available');
      return;
    }
    if (attachment.file_type?.includes('image')) {
      setViewingFile({ ...attachment, url, label: getAttachmentLabel(index) });
    } else {
      // For PDFs and other files, open in new tab
      window.open(url, '_blank');
    }
  };

  return (
    <AdminLayout currentPage="approvals" title="Applications">
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="approvals-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending-apps">
            <Clock className="w-4 h-4 mr-1.5" /> Pending
            {applications.length > 0 && <Badge className="ml-1.5 bg-amber-500 hover:bg-amber-500">{applications.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected-apps">
            <Ban className="w-4 h-4 mr-1.5" /> Rejected
          </TabsTrigger>
        </TabsList>

        {/* PENDING */}
        <TabsContent value="pending" data-testid="pending-apps-content">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : applications.length === 0 ? (
            <Card><CardContent className="pt-6"><p className="text-center text-muted-foreground">No pending applications</p></CardContent></Card>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <Card key={app.application_id} data-testid={`application-${app.application_id}`}>
                  <CardHeader>
                    <CardTitle>{app.title} {app.first_name} {app.last_name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div><p className="text-sm text-muted-foreground">Email</p><p className="break-all">{app.email}</p></div>
                      <div><p className="text-sm text-muted-foreground">Contact</p><p>{app.contact_number}</p></div>
                      <div><p className="text-sm text-muted-foreground">Submitted</p><p>{new Date(app.submitted_at).toLocaleDateString()}</p></div>
                    </div>

                    {app.attachments && app.attachments.length > 0 && (
                      <div className="mb-4 p-4 bg-muted/50 rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Uploaded Documents</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {app.attachments.map((attachment, index) => (
                            <div key={index}
                              className="flex items-center gap-2 p-2 bg-card border rounded cursor-pointer hover:bg-muted transition-colors"
                              onClick={() => openFileViewer(attachment, index)} data-testid={`attachment-${index}`}>
                              {getFileIcon(attachment.file_type)}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{getAttachmentLabel(index)}</p>
                                <p className="text-xs text-muted-foreground truncate">{attachment.filename}</p>
                              </div>
                              <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button variant="outline" onClick={() => setViewingApp(app)} data-testid={`view-${app.application_id}`} className="w-full sm:w-auto">
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </Button>
                      <Button onClick={() => handleReview(app.application_id, 'approved')} className="w-full sm:flex-1" data-testid={`approve-${app.application_id}`}>
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button onClick={() => handleReview(app.application_id, 'rejected')} variant="destructive" className="w-full sm:flex-1" data-testid={`reject-${app.application_id}`}>
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* REJECTED */}
        <TabsContent value="rejected" data-testid="rejected-apps-content">
          {rejectedLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : rejected.length === 0 ? (
            <Card><CardContent className="pt-6"><p className="text-center text-muted-foreground">No rejected applications</p></CardContent></Card>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setShowDeleteAll(true)} data-testid="delete-all-rejected-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete All Rejected ({rejected.length})
                </Button>
              </div>
              {rejected.map((app) => (
                <Card key={app.application_id} data-testid={`rejected-application-${app.application_id}`} className="border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between gap-2">
                      <span>{app.title} {app.first_name} {app.last_name}</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Rejected</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div><p className="text-sm text-muted-foreground">Email</p><p className="break-all">{app.email}</p></div>
                      <div><p className="text-sm text-muted-foreground">Contact</p><p>{app.contact_number}</p></div>
                      <div><p className="text-sm text-muted-foreground">Reviewed</p><p>{app.reviewed_at ? new Date(app.reviewed_at).toLocaleDateString() : '—'}</p></div>
                    </div>
                    {app.review_notes && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm">
                        <p className="text-xs text-red-600 font-medium mb-0.5">Rejection Notes</p>
                        <p className="text-red-800">{app.review_notes}</p>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button variant="outline" onClick={() => setViewingApp(app)} data-testid={`view-rejected-${app.application_id}`} className="w-full sm:w-auto">
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </Button>
                      <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50 w-full sm:w-auto"
                        onClick={() => reconsiderApplication(app)} disabled={reconsidering === app.application_id} data-testid={`reconsider-${app.application_id}`}>
                        {reconsidering === app.application_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
                        Reconsider
                      </Button>
                      <Button variant="destructive" onClick={() => setDeleteTarget(app)} data-testid={`delete-application-${app.application_id}`} className="w-full sm:w-auto">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-application-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" /> Delete Application</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently delete the rejected application for
            <strong> {deleteTarget?.first_name} {deleteTarget?.last_name}</strong>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteApplication} disabled={deleting} data-testid="confirm-delete-application-btn">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Rejected Confirmation */}
      <Dialog open={showDeleteAll} onOpenChange={(o) => !o && setShowDeleteAll(false)}>
        <DialogContent data-testid="delete-all-rejected-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" /> Delete All Rejected Applications</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently delete <strong>all {rejected.length} rejected application(s)</strong>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteAll(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteAllRejected} disabled={deletingAll} data-testid="confirm-delete-all-rejected-btn">
              {deletingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete All Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Application Dialog */}
      <Dialog open={!!viewingApp} onOpenChange={() => setViewingApp(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {viewingApp && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{viewingApp.title} {viewingApp.first_name} {viewingApp.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium break-all">{viewingApp.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{viewingApp.contact_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{viewingApp.date_of_birth}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">NRC/Passport</p>
                  <p className="font-medium">{viewingApp.nrc_passport}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Work Station</p>
                  <p className="font-medium">{viewingApp.work_station}</p>
                </div>
              </div>
              
              {/* Uploaded Files in Dialog */}
              {viewingApp.attachments && viewingApp.attachments.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Uploaded Documents</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {viewingApp.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-3 cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => openFileViewer(attachment, index)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {getFileIcon(attachment.file_type)}
                          <span className="font-medium">{getAttachmentLabel(index)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{attachment.filename}</p>
                        {attachment.file_type?.includes('image') && (
                          <img
                            src={getUploadUrl(attachment.file_path)}
                            alt={getAttachmentLabel(index)}
                            className="mt-2 w-full h-32 object-cover rounded"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {viewingApp.next_of_kin && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Next of Kin</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p>{viewingApp.next_of_kin.first_name} {viewingApp.next_of_kin.surname}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Relationship</p>
                      <p>{viewingApp.next_of_kin.relationship}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Contact</p>
                      <p>{viewingApp.next_of_kin.contact_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p>{viewingApp.next_of_kin.email}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {viewingApp.beneficiaries?.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Beneficiaries</h3>
                  <div className="space-y-2">
                    {viewingApp.beneficiaries.map((ben, idx) => (
                      <div key={idx} className="p-3 border rounded">
                        <p><strong>{ben.full_name}</strong> - {ben.age} years ({ben.relationship})</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewingFile} onOpenChange={() => setViewingFile(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewingFile?.label}</DialogTitle>
          </DialogHeader>
          {viewingFile && (
            <div className="flex flex-col items-center">
              <img
                src={viewingFile.url}
                alt={viewingFile.label}
                className="max-h-[60vh] object-contain rounded"
              />
              <p className="text-sm text-muted-foreground mt-2">{viewingFile.filename}</p>
              <Button
                className="mt-4"
                onClick={() => window.open(viewingFile.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminApprovals;
